<template>
  <div class="flex flex-col bg-gray-100 gap-2" v-if="!loading">
    <div class="grid grid-cols-12">
      <div class="col-span-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div class="bg-white rounded-sm border border-gray-200 p-4 shadow-sm flex flex-col items-center text-center">
          <div class="text-black text-sm font-bold mb-1">
            {{ $t('rationType') }}
          </div>
          <div style="color: rgb(44, 175, 254)" class="font-bold text-lg">
            {{ mostRecentRation() || '-' }}
          </div>
        </div>
        <div class="bg-white rounded-sm border border-gray-200 p-4 shadow-sm flex flex-col items-center text-center">
          <div class="text-black text-sm font-bold mb-1">
            {{ $t('total_free') }}
          </div>
          <div style="color: rgb(44, 175, 254)" class="font-bold text-lg">
            {{formatNumber(getSelectedBatch.silos.reduce((total, silo) => total + (silo.availableWeight || 0), 0))
            }}
          </div>
        </div>
        <div class="bg-white rounded-sm border border-gray-200 p-4 shadow-sm flex flex-col items-center text-center">
          <div class="text-black text-sm font-bold mb-1">
            {{ $t('endOfRationForecast') }}
          </div>
          <div style="color: rgb(44, 175, 254)" class="font-bold text-lg">
            {{ rationEndDate }}
          </div>
        </div>
        <div class="bg-white rounded-sm border border-gray-200 p-4 shadow-sm flex flex-col items-center text-center">
          <div class="text-black text-sm font-bold mb-1">
            {{ $t('durability') }}
          </div>
          <div style="color: rgb(44, 175, 254)" class="font-bold text-lg">
            {{ durability }}
          </div>
        </div>
      </div>
    </div>
    <!-- Filtro dos silos -->
    <div class="bg-white rounded border border-gray-150">
      <DateFilterMenu @filter-change="applyDashboardDateFilter" />
    </div>

    <!-- Silos -->
    <div class="grid grid-cols-12 gap-2">
      <div v-for="silo in getSelectedBatch.silos" :key="'silo_' + silo.id"
        class="bg-white rounded border col-span-12 border-gray-150 p-4 flex flex-col h-full justify-around">
        <div class="grid grid-cols-12 px-4">
          <div class="md:col-span-3 xl:col-span-2 flex flex-col">
            <div class="flex items-center gap-2">
              <span class="font-medium">{{ silo.identification }}</span>
            </div>
            <div class="flex flex-col items-center mt-2 text-xs" style="display: flex; justify-content: space-around;">
              <div class="custom-silo-graph-label">
                <span class="number-big whitespace-nowrap">{{ silo.availableWeight ?
                  formatNumber(silo.availableWeight) + ' kg' : '-' }} </span>
                <span>{{ $t('available') }}</span>
              </div>
              <div class="custom-silo-graph-label">
                <span class="number-big whitespace-nowrap">{{ silo.invoiceDeliveries ?
                  formatNumber(silo.invoiceDeliveries) + ' kg' : '-' }} </span>
                <span style="text-align: center;">{{ $t('total_delivered') }}</span>
              </div>
            </div>
            <div class="p-2 custom-tooltip" style="max-height: 350px;position: relative;"
              :key="'siloGraphDiv_' + silo.id">
              <SiloGraph class="w-full h-full" :key="'siloGraph_' + silo.id"
                :occupationPercent="silo.totalOccupation || 0" :occupationColor="getSiloColor(silo.status)"
                :backgroundColor="getSiloBackgroundColor(silo.status)" />
              <div class="custom-tooltip-tip whitespace-nowrap">
                <div>
                  <strong>{{ $t('available') }}:</strong> {{ silo.availableWeight ?
                    formatNumber(silo.availableWeight) + ' kg' : '-' }}
                </div>
                <div>
                  <strong>{{ $t('free') }}:</strong> {{ silo.freeWeight ?
                    formatNumber(silo.freeWeight) + ' kg' : '-' }}
                </div>
                <div>
                  <strong>{{ $t('quantityTotalDeliveredNf') }}:</strong> {{
                    silo.invoiceDeliveries ? formatNumber(silo.invoiceDeliveries) + ' kg' : '-'
                  }}
                </div>
                <div>
                  <strong>{{ $t('occupation') }}:</strong> {{ silo.totalOccupation ?
                    formatNumber(silo.totalOccupation * 100) + '%' : '-' }}
                </div>
                <div>
                  <strong>{{ $t('rationType') }}:</strong> {{ silo?.currentRation || '-' }}
                </div>
                <div>
                  <strong>{{ $t('status') }}:</strong> {{ silo.status === 3 ? $t('satisfactory') : silo.status ===
                    2 ?
                    $t('alert') : $t('critical') }}
                </div>
              </div>
            </div>
            <div class="mt-2 text-xs" style="text-align: center;">
              {{ $t('last_read') }}: {{ formatDate(silo?.lastReceivedTime) }}
            </div>
          </div>
          <div class="md:col-span-9 xl:col-span-10 relative h-full">
            <ChartComponent v-if="silo.occupationHistory" class="w-full h-full" :id="'siloGraph_' + silo.id" type="xy"
              :graph-props="convertGraphDataHistoryOccupation(silo.occupationHistory).props" />
            <div v-if="silosLoading"
              class="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75">
              <LoadingContent :show="true" :full-page="false" :text="$t('loading')" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Consumption History -->
    <div class="bg-white rounded border border-gray-150 p-4 flex flex-col gap-8">
      <PeriodFilterMenu :batch="getSelectedBatch" v-model="consumptionHistoryGroupBy"
        @filter-change="loadFeedAndWaterHistory" />
      <div class="relative min-h-[300px]">
        <ChartComponent id="rationConsumptionGraph" type="xy" :graph-props="convertGraphDataRationConsumption(
          getSelectedBatch.consumptionHistory,
          consumptionHistoryGroupBy
        ).props || {}" />

        <ChartComponent id="animalConsumptionGraph" type="xy" :graph-props="convertGraphDataAnimalConsumption(
          getSelectedBatch.consumptionHistory,
          consumptionHistoryGroupBy
        ).props || {}" />

        <div v-if="consumptionLoading"
          class="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75">
          <LoadingContent :show="true" :full-page="false" :text="$t('loading')" />
        </div>
      </div>
    </div>

    <!-- Ration End Prediction -->
    <div v-if="getSelectedBatch.rationEndPrediction.length"
      class="bg-white rounded border border-gray-150 p-4 flex flex-col gap-8">
      <ChartComponent id="rationPredictionGraph" type="xy"
        :graph-props="convertGraphRationPrediction(getSelectedBatch.rationEndPrediction).props || {}" />
    </div>

    <!-- Histórico de consumo de água -->
    <section class="bg-white rounded border border-gray-150 p-4">

      <LoadingContent v-if="hydrometryHistoryLoading" :show="true" :full-page="false" :text="$t('loading')" />

      <p v-else-if="hydrometryHistoryError" class="text-red-600">
        {{ $t('water_history_load_error') }}
      </p>

      <ChartComponent v-else-if="hydrometryHistoryPoints.length" id="waterHistoryGraph" type="column"
        :graph-props="hydrometryHistoryChartProps" />

      <p v-else>{{ $t('noData') }}</p>
    </section>

    <!-- Consumo de água -->
    <section class="bg-white rounded border border-gray-150 p-4">
      <h3 class="font-semibold mb-4">{{ $t('water_consumption') }}</h3>

      <LoadingContent v-if="hydrometryLoading" :show="true" :full-page="false" :text="$t('loading')" />

      <p v-else-if="hydrometryError" class="text-red-600">
        {{ $t('water_load_error') }}
      </p>

      <template v-else-if="hydrometry">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div class="border rounded p-4">
            <div class="text-sm text-gray-600">{{ $t('water_total_batch') }}</div>
            <div class="text-xl font-bold">
              {{ formatWater(hydrometry.total_consumption) }}
            </div>
          </div>

          <div class="border rounded p-4">
            <div class="text-sm text-gray-600">{{ $t('water_per_bird') }}</div>
            <div class="text-xl font-bold">
              {{ formatWaterPerBirdLiters(hydrometry.total_consumption_per_bird) }}
            </div>
          </div>

          <div class="border rounded p-4">
            <div class="text-sm text-gray-600">{{ $t('last_read') }}</div>
            <div class="text-xl font-bold">
              {{ hydrometry.last_reading_at
                ? formatDate(hydrometry.last_reading_at)
                : '-' }}
            </div>
          </div>
        </div>

        <ChartComponent v-if="hydrometryHourlyPoints.length" id="waterHourlyGraph" type="column"
          :graph-props="hydrometryHourlyChartProps" />
        <p v-else>{{ $t('noData') }}</p>
      </template>

      <p v-else>{{ $t('noData') }}</p>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';

import SiloGraph from '@/assets/graphs/SiloGraph.vue';
import format from 'date-fns/format';
import ChartComponent from "@/components/dashboard/ChartHighcharts";
import Highcharts from 'highcharts';
import NoDataToDisplay from 'highcharts/modules/no-data-to-display';
import DateFilterMenu from '@/components/dashboard/DateFilterMenu.vue';
import PeriodFilterMenu from '@/components/dashboard/PeriodFilterMenu.vue';
import LoadingContent from '@/components/ui/LoadingContent.vue';

import { formatDistanceToNow } from "date-fns";
import i18n from '@/plugins/i18n';

export default {
  name: 'NutritionContent',
  components: {
    SiloGraph,
    DateFilterMenu,
    PeriodFilterMenu,
    ChartComponent,
    LoadingContent,
  },
  data() {
    return {
      hydrometry: null,
      hydrometryLoading: false,
      hydrometryError: false,
      hydrometryHistory: null,
      hydrometryHistoryLoading: false,
      hydrometryHistoryError: false,
      hydrometryHistoryRequestId: 0,
      quantity: null,
      periodsHistory: [],
      alerts: [],
      siloChartProps: [],
      consumptionHistoryGroupBy: "days",
      silosLoading: false,
      consumptionLoading: false,
      dashboardDateFilter: {
        type: "last_days",
        days: 7
      }
    };
  },
  props: {
    loading: Boolean,
  },
  watch: {
    'getSelectedBatch.id': {
      immediate: true,
      async handler(batchId) {
        if (!batchId) return;

        this.$emit('set-loading', true);

        try {
          await this.loadBatchDetails({
            id: batchId,
            state: 'nutrition'
          });

        } finally {
          this.$emit('set-loading', false);

        }

        this.applyDashboardDateFilter(this.dashboardDateFilter);
        this.loadFeedAndWaterHistory({
          id: batchId,
          start: this.getDateRange(7),
          end: format(new Date(), 'yyyy-MM-dd'),
        });
        this.loadHydrometry(batchId);
      }
    },
  },
  computed: {
    ...mapGetters('environment', ['getSelectedShed', 'getSelectedBatch']),
    hydrometryHourlyPoints() {
      const rows = this.hydrometry?.data;
      if (!Array.isArray(rows)) return [];

      return rows
        .filter(row => row.timestamp && row.consumption != null)
        .map(row => ({
          x: new Date(row.timestamp).getTime(),
          y: Number(row.consumption),
        }))
        .filter(point => Number.isFinite(point.x) && Number.isFinite(point.y))
        .sort((a, b) => a.x - b.x);
    },
    hydrometryHourlyChartProps() {
      const locale = this.$i18n.locale;

      return {
        chart: { type: 'column', height: 320 },
        time: { useUTC: false },
        title: { text: this.$t('water_hourly_24h') },
        credits: { enabled: false },
        legend: { enabled: false },
        xAxis: {
          type: 'datetime',
          title: { text: this.$t('dateTime') },
          labels: { format: '{value:%d/%m %H:%M}' },
        },
        yAxis: {
          min: 0,
          title: { text: 'L' },
        },
        tooltip: {
          formatter: function () {
            const value = Number(this.y).toLocaleString(locale, {
              maximumFractionDigits: 2,
            });
            return `${format(new Date(this.x), 'dd/MM/yyyy HH:mm')}<br><b>${value} L</b>`;
          },
        },
        series: [{
          name: this.$t('water_consumption'),
          type: 'column',
          color: '#2CAFFE',
          data: this.hydrometryHourlyPoints,
        }],
      };
    },
    hydrometryHistoryPoints() {
      const rows = this.hydrometryHistory?.data;
      if (!Array.isArray(rows)) return [];

      const dailyPoints = rows
        .filter(row =>
          row.age != null &&
          row.consumption != null &&
          row.consumption_per_bird != null
        )
        .map(row => ({
          x: Number(row.age),
          week: row.week == null ? null : Number(row.week),
          total: Number(row.consumption),
          perBird: Number(row.consumption_per_bird) * 1000,
        }))
        .filter(row =>
          Number.isFinite(row.x) &&
          Number.isFinite(row.total) &&
          Number.isFinite(row.perBird)
        )
        .sort((a, b) => a.x - b.x);

      if (this.consumptionHistoryGroupBy !== 'weeks') return dailyPoints;

      const weeklyPoints = new Map();
      dailyPoints.forEach(row => {
        if (!Number.isFinite(row.week)) return;

        if (!weeklyPoints.has(row.week)) {
          weeklyPoints.set(row.week, { x: row.week, total: 0, perBird: 0 });
        }

        const point = weeklyPoints.get(row.week);
        point.total += row.total;
        point.perBird += row.perBird;
      });

      return Array.from(weeklyPoints.values()).sort((a, b) => a.x - b.x);
    },
    hydrometryHistoryChartProps() {
      const points = this.hydrometryHistoryPoints;
      const locale = this.$i18n.locale;
      const weekly = this.consumptionHistoryGroupBy === 'weeks';
      const periodLabel = this.$t(weekly ? 'week' : 'age');
      const totalName = this.$t(weekly ? 'water_weekly_total' : 'water_daily_total');
      const perBirdName = this.$t(weekly ? 'water_weekly_per_bird' : 'water_daily_per_bird');
      const perBirdUnit = this.$t('water_milliliters_per_bird');
      const maxPerBird = Math.max(0, ...points.map(row => row.perBird));
      const maxTotal = Math.max(0, ...points.map(row => row.total));
      const maxPerBirdToTotalRatio = points.reduce((max, row) =>
        row.total > 0 ? Math.max(max, row.perBird / row.total) : max, 0);
      const totalAxisMax = maxTotal * 1.1;
      const perBirdAxisMax = Math.max(maxPerBird * 1.1, totalAxisMax * maxPerBirdToTotalRatio * 2);

      return {
        chart: { height: 360 },
        title: { text: this.$t('water_history') },
        credits: { enabled: false },
        legend: { enabled: true },
        xAxis: {
          title: { text: periodLabel },
          allowDecimals: false,
        },
        yAxis: [
          { min: 0, max: totalAxisMax || undefined, endOnTick: false, title: { text: 'L' } },
          { min: 0, max: perBirdAxisMax || undefined, endOnTick: false, title: { text: perBirdUnit }, opposite: true },
        ],
        tooltip: {
          formatter: function () {
            const isPerBird = this.series.name === perBirdName;
            const digits = isPerBird ? 1 : 0;
            const unit = isPerBird ? perBirdUnit : 'L';
            const value = Number(this.y).toLocaleString(locale, {
              minimumFractionDigits: digits,
              maximumFractionDigits: digits,
            });

            return `${periodLabel}: ${this.x}<br><b>${this.series.name}: ${value} ${unit}</b>`;
          },
        },
        series: [
          {
            name: totalName,
            type: 'column',
            yAxis: 0,
            color: '#2CAFFE',
            data: points.map(row => [row.x, row.total]),
          },
          {
            name: perBirdName,
            type: 'line',
            yAxis: 1,
            color: '#D6B133',
            data: points.map(row => [row.x, row.perBird]),
          },
        ],
      };
    },
    durability: {
      get() {
        const dates = this.getSelectedBatch.rationEndPrediction;
        const last_date = dates.length ? new Date(dates[dates.length - 1].timestamp) : null
        return dates.length ? formatDistanceToNow(last_date, { addSuffix: true, locale: this.$t('durability-language') }) : '-'
      }
    },
    rationEndDate: {
      get() {
        const resultMap = this.getSelectedBatch.rationEndPrediction;
        if (!Array.isArray(resultMap) || resultMap.length === 0) return '-';

        const lastEntry = resultMap[resultMap.length - 1];
        if (!lastEntry?.timestamp) return '-';

        try {
          return format(new Date(lastEntry.timestamp), 'dd/MM/yyyy HH:mm');
        } catch (e) {
          return '-';
        }
      }
    },
    convertGraphDataHistoryOccupation(series) {
      return (series) => {
        let trackingSeries = [];
        let anniversarySeries = [];
        const dayInMs = 1000 * 60 * 60 * 24;

        if (Array.isArray(series) && series.length > 0) {
          trackingSeries = series.flatMap(item => {
            if (!Array.isArray(item.history) || !item.shed?.batch?.last_anniversary) {
              return [];
            }

            const lastAnniversary = new Date(item.shed.batch.last_anniversary);
            if (isNaN(lastAnniversary)) return [];

            return item.history.map(history => {
              const deviceTime = new Date(history.available?.message?.device_time);
              if (isNaN(deviceTime)) return null;

              const timeDiffMs = deviceTime - lastAnniversary;
              const timeDiffDays = timeDiffMs / dayInMs;

              const baseAge = item.shed?.batch?.age ?? 0;
              const age = baseAge + timeDiffDays;

              const matchingDelivery = item.deliveries?.find(delivery => {
                if (!delivery.delivery_date) return false;
                const deliveryDate = new Date(delivery.delivery_date);
                return deviceTime.getTime() === deliveryDate.getTime();
              });

              let deliveryWeight = null;
              if (matchingDelivery) {
                const linkedWeight = matchingDelivery.linked_invoice?.weight ?? 0;
                deliveryWeight =
                  linkedWeight !== 0
                    ? linkedWeight
                    : matchingDelivery.weight ?? null;
              }

              return {
                ...item,
                history,
                deliveryWeight,
                x: age,
                y: history.document_state?.is_blackout
                  ? null
                  : history.document_state?.occupancy ?? 0
              };
            });
          }).filter(Boolean);

          const intervals = trackingSeries.length
            ? this.getAgeIntervals(trackingSeries)
            : [];

          anniversarySeries = series
            .filter(item => item.shed?.batch?.age != null)
            .map(item => {
              const lastAnniversary = new Date(item.shed.batch.last_anniversary);
              const latestHistory = item.history?.[item.history.length - 1];

              const lastDeviceTime = latestHistory?.available?.message?.device_time
                ? new Date(latestHistory.available.message.device_time)
                : lastAnniversary;

              const timeDiffDays = (lastDeviceTime - lastAnniversary) / dayInMs;
              const age = item.shed.batch.age + timeDiffDays;
              const alignedAge = Math.ceil(age);

              const occupancy =
                item.current?.document_state?.occupancy ??
                item.current?.occupancy ??
                null;

              return {
                ...item,
                x: alignedAge,
                y: occupancy != null ? Number(occupancy) : null,
                batch: item.shed.batch,
                last_anniversary: item.shed.batch.last_anniversary
              };
            });

          anniversarySeries = intervals.length
            ? this.filterByIntervals(anniversarySeries, intervals)
            : anniversarySeries;
        }

        NoDataToDisplay(Highcharts)

        return {
          props: {

            lang: {
              noData: this.$t('noData'),
            },
            noData: {
              style: {
                fontWeight: 'bold',
                fontSize: '14px',
                color: '#606060'
              }
            },

            periodFrom: null,
            periodTo: null,
            credits: { enabled: false },
            legend: { enabled: false },
            title: { text: i18n.t('occupation_history1') },
            yAxis: {
              max: 1,
              title: { text: i18n.t('occupation%'), enabled: false },
              labels: {
                formatter: function () {
                  return (this.value * 100).toFixed(0) + '%';
                }
              }
            },
            tooltip: {
              borderColor: '#2c3e50',
              shared: false,
              formatter: function () {
                let rt = ''
                if (this.point.series.name === "Série 1") {
                  if (this.point.history.available.message.device_time != undefined) {
                    rt += '<span><strong>' + i18n.t('dateTime') + ':</strong> ' + (format(new Date(this.point.history.available.message.device_time), "dd/MM/yyyy H:mm:ss") || '-') + '</span>' + '<br/>'
                  }
                  if (this.point.y != undefined) {
                    rt += '<span><strong>' + i18n.t('occupation') + ':</strong> ' + (this.point.y * 100).toFixed(0) + '%</span>' + '<br/>'
                  }
                  if (this.point.history.document_state.weight != undefined) {
                    rt += '<span><strong>' + i18n.t('available') + ':</strong> ' + (this.point.history.document_state.weight || 0).toFixed(2) + 'kg</span>' + '<br/>'
                  }
                  if (this.point.history.document_state.free_weight_capacity != undefined) {
                    rt += '<span><strong>' + i18n.t('free') + ':</strong> ' + (this.point.history.document_state.free_weight_capacity || 0).toFixed(2) + 'kg</span>' + '<br/>'
                  }
                  if (this.point.history.document_state.consuming_ration != undefined) {
                    rt += '<span><strong>' + i18n.t('ration') + ':</strong> ' + (this.point.history.document_state.consuming_ration || 0) + '</span>' + '<br/>'
                  }
                  if (this.point.x != undefined) {
                    rt += '<span><strong>' + i18n.t('age') + ':</strong> ' + (this.point.x || 0).toFixed(0) + '</span>' + '<br/>'
                    rt += '<span><strong>' + i18n.t('week') + ':</strong> ' + Math.floor(this.point.x / 7) + '</span>' + '<br/>'
                  }
                  if (this.point.deliveryWeight != null) {
                    rt += '<span style="color:rgb(235, 110, 96);"><strong>' + i18n.t('deliveryWeight') + ':</strong> '
                      + this.point.deliveryWeight.toFixed(2) + 'kg</span><br/>';
                  }
                } else {
                  const anniversary = this.point.last_anniversary || this.point.shed?.batch?.last_anniversary;
                  if (anniversary != null) {
                    rt += '<span><strong>' + i18n.t('dateTime') + ':</strong> ' + (format(new Date(new Date(anniversary).getTime() + 24 * 60 * 60 * 1000), "dd/MM/yyyy H:mm:ss") || '-') + '</span>' + '<br/>'
                  }
                  if (this.point.y != null) {
                    rt += '<span><strong>' + i18n.t('occupation') + ':</strong> ' + (this.point.y * 100).toFixed(0) + '%</span>' + '<br/>'
                  }
                  if (this.point.x != null) {
                    rt += '<span><strong>' + i18n.t('week') + ':</strong> ' + Math.floor(this.point.x / 7) + '</span>' + '<br/>'
                  }
                }
                return rt;
              }
            },
            plotOptions: { column: { pointWidth: 20 }, line: { marker: { radius: 2 } } },
            xAxis: [{
              title: {
                text: i18n.t('age')
              },
              tickInterval: 1,
              labels: {
                step: 1,
                rotation: 0,
                formatter: function () {
                  const label = this.axis.defaultLabelFormatter.call(this);
                  return (parseFloat(label) % 1 === 0 ? label : '');
                }
              }
            }],
            series: (trackingSeries.length || anniversarySeries.length)
              ? [
                { name: 'Série 1', type: 'line', step: 'start', data: trackingSeries },
                { name: 'Série 2', color: '#D6B133', type: 'column', pointPlacement: 'on', data: anniversarySeries },
              ]
              : [{ name: i18n.t('noData'), type: 'line', data: [] }],
          }
        };
      }
    },
    convertGraphDataRationConsumption() {
      return (series, groupBy) => {
        if (!series || !series.length) {
          return {};
        }
        let filteredSeries = series.map(item => ({
          ...item,
          x: item.age
        })).filter(item => item.x <= this.getSelectedBatch.age);

        if (groupBy === 'weeks') {
          filteredSeries = this.groupByWeek(filteredSeries);
        }

        return {
          props: {
            periodFrom: null,
            periodTo: null,
            credits: { enabled: false },
            legend: {
              enabled: true,
              layout: 'horizontal',
              align: 'center',
              verticalAlign: 'top',
            },
            title: {
              text: this.$t('rationConsumptionXIdealConsumption'),
            },
            yAxis: {
              labels: {
                formatter: function () {
                  return this.value + ' ' + i18n.t(' kg');
                }
              },
              title: {
                text: this.$t('ration-consumption'),
                enabled: false
              }
            },
            xAxis: {
              title: {
                text: i18n.t('age')
              }
            },
            plotOptions: {
              line: {
                marker: {
                  radius: 2
                }
              }
            },
            tooltip: {
              borderColor: '#2c3e50',
              shared: false,
              formatter: function () {
                const date = this.point.last_anniversary
                  ? format(new Date(new Date(this.point.last_anniversary).getTime() + 24 * 60 * 60 * 1000), "dd/MM/yyyy H:mm:ss")
                  : '-'

                let label = ''
                if (groupBy === "days" && this.point.series.name === i18n.t('real')) {
                  label += `<span><strong>${i18n.t('dateTime')}:</strong> ${date}</span><br/>`
                }
                label += `<span><strong>${groupBy === 'weeks' ? i18n.t('week') : i18n.t('age')}:</strong> ${this.x}</span><br/>`
                label += `<span><strong>${this.series.name}:</strong> ${Math.floor(this.y)}  kg</span>`

                return label
              }
            },
            series: [
              {
                name: i18n.t('real'),
                color: '#D6B133',
                type: 'column',
                data: filteredSeries.map(item => ({ ...item, y: item.amount }))
              },
              {
                name: i18n.t('reference'),
                color: '#5C94D9',
                type: 'column',
                data: filteredSeries.map(item => ({ ...item, y: item.ideal_amount }))
              }
            ]
          }
        }
      }
    },
    convertGraphDataAnimalConsumption() {
      return (series, groupBy) => {

        if (!series || !series.length) {
          return {};
        }
        let filteredSeries = series.map(item => ({
          ...item,
          x: item.age
        })).filter(item => item.x <= this.getSelectedBatch.age);

        if (groupBy === 'weeks') {
          filteredSeries = this.groupByWeek(filteredSeries);
        }

        return {
          props: {
            periodFrom: null,
            periodTo: null,
            credits: { enabled: false },
            legend: {
              enabled: true,
              layout: 'horizontal',
              align: 'center',
              verticalAlign: 'top',
            },
            title: {
              text: this.$t('consumptionByAnimal'),
            },
            yAxis: {
              labels: {
                formatter: function () {
                  return this.value + ' ' + i18n.t('g');
                }
              },
              title: {
                text: this.$t('consumptionByAnimal'),
                enabled: false
              }
            },
            xAxis: {
              title: {
                text: i18n.t('age')
              }
            },
            plotOptions: {
              line: {
                marker: {
                  radius: 2
                }
              }
            },
            tooltip: {
              borderColor: '#2c3e50',
              shared: false,
              formatter: function () {
                const date = this.point.last_anniversary
                  ? format(new Date(new Date(this.point.last_anniversary).getTime() + 24 * 60 * 60 * 1000), "dd/MM/yyyy H:mm:ss")
                  : '-'
                let label = ''
                if (groupBy === "days" && this.point.series.name === i18n.t('real')) {
                  label += `<span><strong>${i18n.t('dateTime')}:</strong> ${date}</span><br/>`
                }
                label += `<span><strong>${groupBy === 'weeks' ? i18n.t('week') : i18n.t('age')}:</strong> ${this.x}</span><br/>`
                label += `<span><strong>${this.series.name}:</strong> ${this.point.series.name === i18n.t('real') ? this.y.toFixed(2) : Math.floor(this.y)} g</span>`

                return label
              }
            },
            series: [
              {
                name: i18n.t('real'),
                color: '#ffde59',
                type: 'column',
                data: filteredSeries.map(item => ({ ...item, y: item.amount / item.current_lodged * 1000 }))
              },
              {
                name: i18n.t('reference'),
                color: '#ed5c00',
                type: 'column',
                data: filteredSeries.map(item => ({ ...item, y: item.ideal_amount / item.current_lodged * 1000 }))
              }
            ]
          }
        }
      }
    },
    convertGraphRationPrediction() {
      return (series) => {

        if (!series || !series.length) {
          return {};
        }

        return {
          props: {
            credits: { enabled: false },
            legend: {
              enabled: true,
              layout: 'horizontal',
              align: 'center',
              verticalAlign: 'top',
            },
            title: {
              text: this.$t('endOfRationForecast'),
            },
            yAxis: {
              title: {
                text: this.$t('availableRation'),
                enabled: false
              }
            },
            xAxis: {
              type: 'datetime',
              labels: {
                format: '{value:%d/%m %H:%M}',
              }
            },
            plotOptions: {
              line: {
                marker: {
                  radius: 2
                }
              }
            },
            tooltip: {
              formatter: function () {
                let label = ''
                label += `<span><strong>${i18n.t('dateTime')}:</strong> ${format(new Date(this.x), "dd/MM/yyyy H:mm:ss")}</span><br/>`
                label += `<span><strong>${i18n.t('availableRation')}:</strong> ${this.y.toFixed(2) ?? '0.00'}  kg</span>`
                return label
              }
            },
            series: [
              {
                name: i18n.t('total'),
                color: '#D6B133',
                type: 'line',
                data: series.map(item => ({ ...item, x: item.timestamp, y: item.amount }))
              }
            ]
          }
        }
      }
    },
  },
  methods: {
    ...mapActions('environment', ['loadBatchDetails', 'loadBatchConsumptionHistory', 'loadBatchSiloOccupationHistory']),
    ...mapMutations('nutrition', ['SET_SELECTED_SILO']),
    ...mapActions('nutrition', ['loadBatchHydrometry', 'loadBatchHydrometryHistory']),

    loadFeedAndWaterHistory({ id, start, end }) {
      if (!id || !start || !end || start > end) return;

      const params = { start, end };
      this.loadConsumptionHistory(params);
      this.loadHydrometryHistory(id, params);
    },

    async loadHydrometryHistory(batchId, params) {
      const requestId = ++this.hydrometryHistoryRequestId;
      this.hydrometryHistory = null;
      this.hydrometryHistoryLoading = true;
      this.hydrometryHistoryError = false;

      try {
        const response = await this.loadBatchHydrometryHistory({
          batchId,
          ...params,
        });

        if (requestId === this.hydrometryHistoryRequestId &&
          this.getSelectedBatch.id === batchId) {
          this.hydrometryHistory = response;
        }
      } catch (error) {
        if (requestId === this.hydrometryHistoryRequestId &&
          this.getSelectedBatch.id === batchId) {
          this.hydrometryHistoryError = true;
        }
      } finally {
        if (requestId === this.hydrometryHistoryRequestId &&
          this.getSelectedBatch.id === batchId) {
          this.hydrometryHistoryLoading = false;
        }
      }
    },

    async loadHydrometry(batchId) {
      this.hydrometry = null;
      this.hydrometryLoading = true;
      this.hydrometryError = false;

      try {
        const response = await this.loadBatchHydrometry(batchId);
        if (this.getSelectedBatch.id === batchId) {
          this.hydrometry = response;
        }
      } catch (error) {
        if (this.getSelectedBatch.id === batchId) {
          this.hydrometryError = true;
        }
      } finally {
        if (this.getSelectedBatch.id === batchId) {
          this.hydrometryLoading = false;
        }
      }
    },
    formatWater(value, decimalPlaces = 0) {
      if (value == null || !Number.isFinite(Number(value))) return '-';

      return `${Number(value).toLocaleString(this.$i18n.locale, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      })} L`;
    },
    formatWaterPerBirdLiters(value) {
      if (value == null || !Number.isFinite(Number(value))) return '-';

      return `${Number(value).toLocaleString(this.$i18n.locale, {
        maximumFractionDigits: 4,
      })} ${this.$t('water_liters_per_bird')}`;
    },
    async applyDashboardDateFilter(filter) {
      const batch = this.getSelectedBatch;

      if (!batch?.id) return;

      this.dashboardDateFilter = { ...filter };
      this.silosLoading = true;

      const siloFilter = filter.type === "last_days"
        ? { days: filter.days }
        : {
          start: filter.start,
          end: filter.end
        };

      try {
        const siloRequests = batch.silos.map(silo =>
          this.loadBatchSiloOccupationHistory({
            id: silo.id,
            batch_id: batch.id,
            ...siloFilter
          })
        );

        await Promise.all(siloRequests);
      } finally {
        this.silosLoading = false;
      }
    },
    async loadConsumptionHistory(filter) {
      this.consumptionLoading = true;

      try {
        await this.loadBatchConsumptionHistory(filter);
      } finally {
        this.consumptionLoading = false;
      }
    },
    getSiloColor(number) {
      if (number == 3) return '#00FF00';
      if (number == 1) return '#FF0000';
      if (number == 2) return '#FFF000';
      return '#FFFFFF';
    },
    getSiloBackgroundColor(number) {
      if (number == 3) return 'rgba(102, 255, 102, 0.2)';
      if (number == 1) return 'rgba(255, 102, 102, 0.2)';
      if (number == 2) return 'rgba(255, 255, 102, 0.2)';
      return 'rgba(255, 255, 255, 1)';
    },
    mostRecentRation() {
      let silos = this.getSelectedBatch.silos.filter(silo => silo && silo.lastReceivedTime);

      silos.sort((a, b) => b.parsedDate - a.parsedDate);
      return silos.length ? silos[0].currentRation : null;
    },
    getAgeIntervals(trackingSeries) {
      if (!trackingSeries.length) return [];

      const ages = trackingSeries
        .map(i => Math.floor(i.x))
        .filter((value, index, self) => self.indexOf(value) === index)
        .sort((a, b) => a - b);

      const intervals = [];
      let start = ages[0];
      let prev = ages[0];

      for (let i = 1; i < ages.length; i++) {
        if (ages[i] !== prev + 1) {
          intervals.push({ min: start, max: prev });
          start = ages[i];
        }
        prev = ages[i];
      }

      intervals.push({ min: start, max: prev });

      return intervals;
    },
    filterByIntervals(serie, intervals) {
      return intervals
        .map(({ min, max }) => {
          return serie.filter(item => {
            const x = item.x;
            return x >= min && x <= max;
          });
        })
        .flat(1);
    },
    groupByWeek(data) {
      const grouped = [];

      data.forEach(item => {
        const { week } = item
        if (!grouped[week]) {
          grouped[week] = { week, amount: 0, ideal_amount: 0, current_lodged: 0 };
        }
        grouped[week].amount += item.amount || 0;
        grouped[week].ideal_amount += item.ideal_amount || 0;
        grouped[week].current_lodged += item.current_lodged || 0;
      });

      return Object.values(grouped).map(g => ({
        x: g.week,
        amount: g.amount,
        ideal_amount: g.ideal_amount,
        current_lodged: g.current_lodged
      }));
    },
    async loadAlertsFromStore(item) {
      const shedId = item?.id || this.getSelectedShed?.id;
      if (!shedId) return;
      const alerts = await this.$store.dispatch('nutrition/loadAlerts', shedId);
      this.alerts = alerts;
    },
    fnMouseMoveCustomTooltip(e) {
      var tooltip = document.querySelectorAll('.custom-tooltip-tip');

      for (var i = tooltip.length; i--;) {
        tooltip[i].style.left = e.clientX - 20 + 'px';
        tooltip[i].style.top = e.clientY - 50 + 'px';
      }
    },
    formatNumber(number) {
      if (typeof number != 'number' || !i18n.locale) {
        return number;
      }
      return Math.round(number).toLocaleString(i18n.locale);
    },
    formatDate(date) {
      if (date)
        return format(new Date(date), "dd/MM/yyyy H:mm:ss")
    },
    getDateRange(days) {
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() - days);
      return targetDate.toISOString().slice(0, 10);
    },
  },
  async created() {
    document.addEventListener('mousemove', this.fnMouseMoveCustomTooltip, false);
  },
};
</script>
